import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Lock, LogIn, Mail, Trophy } from 'lucide-react';
import { useState, type CSSProperties, type FormEvent } from 'react';
import type { PublicCustomDomainInfo } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { resolveHomePagePath } from '../lib/home-page';
import { Alert, Button } from '../components/ui';
import { AuthCard, AuthInput, AuthLabel } from '../components/auth-ui';
import { LegalLinks } from '../components/LegalLinks';

/** Login form fields alone, shared by both layouts below — identical
 * behaviour regardless of which domain got us here. */
function LoginFields({
  email,
  password,
  onEmail,
  onPassword,
  error,
  busy,
  onSubmit,
}: {
  email: string;
  password: string;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  error: string | null;
  busy: boolean;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {error && <Alert>{error}</Alert>}
      <div>
        <AuthLabel>E-Mail</AuthLabel>
        <AuthInput
          icon={Mail}
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          placeholder="du@verein.de"
          required
        />
      </div>
      <div>
        <AuthLabel>Passwort</AuthLabel>
        <AuthInput
          icon={Lock}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => onPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
      </div>
      <Button type="submit" disabled={busy} className="w-full gap-2 py-2.5">
        {busy ? (
          'Wird angemeldet…'
        ) : (
          <>
            <LogIn size={16} /> Anmelden
          </>
        )}
      </Button>
    </form>
  );
}

const forgotPasswordLink = (
  <a href="/forgot-password" className="font-medium text-brand-accent hover:underline">
    Passwort vergessen?
  </a>
);

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Reached via a club's own domain (Verwaltung › Verein › Eigene Domain)?
  // Resolved purely from the request's own Host header server-side — never
  // 404s loudly, just falls back to the neutral AuthCard below when there's
  // no match (the overwhelmingly common case: the shared platform domain).
  const { data: domainInfo } = useQuery({
    queryKey: ['public', 'custom-domain'],
    queryFn: () => api<PublicCustomDomainInfo>('/public/custom-domain').catch(() => null),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/auth/login', { method: 'POST', body: { email, password } });
      const fresh = await refresh();
      await navigate({ to: resolveHomePagePath(fresh) });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Anmeldung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  if (domainInfo) {
    const design = domainInfo.authDesign;
    const cssVars = design.primaryColor ? ({ '--brand-accent': design.primaryColor } as CSSProperties) : undefined;
    const pageBg = design.backgroundColor ? { backgroundColor: design.backgroundColor } : undefined;
    const logoUrl = domainInfo.logoFileId ? '/api/public/custom-domain/logo' : null;
    const heroImageUrl = domainInfo.authImageFileId ? '/api/public/custom-domain/auth-image' : null;

    return (
      <div className="flex min-h-full flex-col md:flex-row" style={{ ...cssVars, ...pageBg }}>
        {/* Same split-layout language as the registration page (InvitePage.tsx)
         * — the whole point of a custom domain is that this now *can* be
         * personalized, unlike the shared-domain login below. */}
        <div className="relative flex min-h-[200px] shrink-0 items-end overflow-hidden bg-gradient-to-br from-brand-accent to-indigo-700 md:min-h-full md:w-1/2">
          {heroImageUrl && <img src={heroImageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)' }}
          />
          <div className="relative z-10 p-6 text-white sm:p-10">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt=""
                className="mb-4 h-14 w-14 rounded-2xl border border-white/40 bg-white object-contain p-1.5 shadow-lg"
              />
            ) : (
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 shadow-lg backdrop-blur-sm">
                <Trophy size={26} />
              </div>
            )}
            <h1 className="text-2xl font-bold sm:text-3xl">{design.heading || domainInfo.organizationName}</h1>
            {design.welcomeText && <p className="mt-2 max-w-sm text-sm text-white/85 sm:text-base">{design.welcomeText}</p>}
          </div>
        </div>

        <div className="flex flex-1 items-center justify-center p-4 py-8 sm:p-8">
          <div className="w-full max-w-md">
            <div className="rounded-2xl border border-white/60 bg-white/95 p-6 shadow-xl shadow-slate-900/5 backdrop-blur-sm sm:p-8">
              <h2 className="mb-5 text-lg font-semibold text-slate-900">Willkommen zurück</h2>
              <LoginFields
                email={email}
                password={password}
                onEmail={setEmail}
                onPassword={setPassword}
                error={error}
                busy={busy}
                onSubmit={submit}
              />
              <p className="mt-4 text-center text-sm text-slate-500">{forgotPasswordLink}</p>
            </div>
            <LegalLinks className="mt-4" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <AuthCard
      name="Badminton Manager"
      title="Willkommen zurück"
      subtitle="Melde dich an, um auf deinen Verein zuzugreifen."
      footer={
        <>
          {forgotPasswordLink}
          <LegalLinks className="mt-3" />
        </>
      }
    >
      <LoginFields
        email={email}
        password={password}
        onEmail={setEmail}
        onPassword={setPassword}
        error={error}
        busy={busy}
        onSubmit={submit}
      />
    </AuthCard>
  );
}
